'use client';

import { usePathname } from 'next/navigation';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Navigation state that can be passed between routes
 * This replaces react-router-dom's location.state
 */
export interface NavigationState {
  returnTo?: string;
  orderNumber?: number;
  [key: string]: unknown;
}

interface NavigationStateContextValue {
  /**
   * Get the current navigation state
   */
  state: NavigationState | null;

  /**
   * Set state for the next navigation
   * Call this before navigating to pass state to the target route
   */
  setStateForNavigation: (state: NavigationState | null) => void;

  /**
   * Clear the navigation state
   * Useful when you've consumed the state and want to prevent it from being reused
   */
  clearState: () => void;
}

const NavigationStateContext = createContext<NavigationStateContextValue | null>(null);

interface NavigationStateProviderProps {
  children: ReactNode;
}

/**
 * NavigationStateProvider
 *
 * Provides a context-based mechanism for passing state between routes,
 * replacing react-router-dom's location.state functionality.
 *
 * Usage:
 *
 * 1. Before navigating, set the state:
 * ```tsx
 * const { setStateForNavigation } = useNavigationState();
 * setStateForNavigation({ returnTo: '/previous-page' });
 * router.push('/target-page');
 * ```
 *
 * 2. On the target page, read and clear the state:
 * ```tsx
 * const { state, clearState } = useNavigationState();
 * useEffect(() => {
 *   if (state?.returnTo) {
 *     // Use the state
 *     clearState(); // Clear after use
 *   }
 * }, [state]);
 * ```
 */
export function NavigationStateProvider({ children }: NavigationStateProviderProps) {
  const pathname = usePathname();
  const [state, setState] = useState<NavigationState | null>(null);
  const pendingStateRef = useRef<NavigationState | null>(null);
  const lastPathnameRef = useRef<string>(pathname);

  // When pathname changes, apply any pending state
  useEffect(() => {
    if (pathname !== lastPathnameRef.current) {
      // Apply pending state to current state
      if (pendingStateRef.current !== null) {
        setState(pendingStateRef.current);
        pendingStateRef.current = null;
      } else {
        // Clear state on navigation without pending state
        // This prevents stale state from persisting across navigations
        setState(null);
      }
      lastPathnameRef.current = pathname;
    }
  }, [pathname]);

  const setStateForNavigation = useCallback((newState: NavigationState | null) => {
    pendingStateRef.current = newState;
  }, []);

  const clearState = useCallback(() => {
    setState(null);
    pendingStateRef.current = null;
  }, []);

  const value = useMemo(
    () => ({
      state,
      setStateForNavigation,
      clearState,
    }),
    [state, setStateForNavigation, clearState],
  );

  return (
    <NavigationStateContext.Provider value={value}>{children}</NavigationStateContext.Provider>
  );
}

/**
 * Hook to access the navigation state context
 *
 * @throws Error if used outside of NavigationStateProvider
 */
export function useNavigationState(): NavigationStateContextValue {
  const context = useContext(NavigationStateContext);

  if (!context) {
    throw new Error('useNavigationState must be used within a NavigationStateProvider');
  }

  return context;
}

/**
 * Hook to get a navigate function that supports state passing
 * This combines router.push with state setting for convenient usage
 */
export function useNavigateWithState() {
  const { setStateForNavigation } = useNavigationState();

  const navigateWithState = useCallback(
    (
      navigate: (path: string, options?: { replace?: boolean; scroll?: boolean }) => void,
      path: string,
      options?: { replace?: boolean; scroll?: boolean; state?: NavigationState },
    ) => {
      if (options?.state) {
        setStateForNavigation(options.state);
      }
      const { state: _, ...navOptions } = options ?? {};
      navigate(path, navOptions);
    },
    [setStateForNavigation],
  );

  return navigateWithState;
}

export default NavigationStateProvider;
